import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "../lib/api";
import { DICT, FUELS, GEARBOXES, BODIES } from "../lib/i18n";
import { AdminShell } from "../components/admin/AdminShell";
import { PhotoManager } from "../components/admin/PhotoManager";
import { DescriptionTranslator } from "../components/admin/DescriptionTranslator";
import { Switch } from "../components/ui/switch";

const EMPTY = {
  make: "", model: "", version: "", price: "", km: "", year: "", cv: "", kw: "", color: "", vin: "", plate: "",
  fuel: "", gearbox: "", body: "", doors: "", seats: "", engine_cc: "", owners: "", registration: "", description: "", description_i18n: {}, version_i18n: {}, color_i18n: {},
  photos: [], status: "available", visible: true, featured: false,
};
const NUMS = ["price", "km", "year", "cv", "kw", "doors", "seats", "engine_cc", "owners"];
const it = DICT.it;
const inputCls = "h-11 w-full rounded-xl border border-ink/15 bg-white px-4 text-ink outline-none transition-colors focus:border-champagne";

const Field = ({ label, name, form, set, type = "text", options, placeholder }) => (
  <label className="block">
    <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-600">{label}</span>
    {options ? (
      <select value={form[name] ?? ""} onChange={(e) => set(name, e.target.value)} className={`${inputCls} mt-1.5`} data-testid={`car-field-${name}`}>
        <option value="">—</option>
        {Object.entries(options).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
    ) : (
      <input type={type} inputMode={type === "number" ? "numeric" : undefined} value={form[name] ?? ""} placeholder={placeholder}
        onChange={(e) => set(name, e.target.value)} className={`${inputCls} mt-1.5`} data-testid={`car-field-${name}`} />
    )}
  </label>
);

const Block = ({ title, children }) => (
  <section className="rounded-2xl border border-ink/10 bg-white p-5 sm:p-7">
    <h2 className="font-display text-lg font-extrabold uppercase tracking-tight text-ink">{title}</h2>
    <div className="mt-5">{children}</div>
  </section>
);

const pick = (keys, dict) => Object.fromEntries(keys.map((k) => [k, dict[k]]));

export default function AdminCarEdit() {
  const { id } = useParams();
  const isNew = !id || id === "nuova";
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (isNew) return;
    http.get(`/admin/cars/${id}`)
      .then((r) => setForm({ ...EMPTY, ...Object.fromEntries(Object.entries(r.data).map(([k, v]) => [k, v ?? (k.endsWith("_i18n") ? {} : "")])) }))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form };
    NUMS.forEach((k) => {
      const v = String(payload[k] ?? "").replace(/[^\d.,]/g, "").replace(/\./g, "").replace(",", ".");
      payload[k] = v === "" ? null : Number(v);
    });
    try {
      if (isNew) await http.post("/admin/cars", payload);
      else await http.put(`/admin/cars/${id}`, payload);
      toast.success("Auto salvata");
      navigate("/admin/dashboard");
    } catch (ex) {
      toast.error(errMsg(ex));
    } finally {
      setSaving(false);
    }
  };

  const f = { form, set };
  const saveBtn = (
    <button type="submit" form="car-form" disabled={saving} className="btn-ink" data-testid="admin-save-car-button">
      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva
    </button>
  );

  if (loading) return <AdminShell><p className="text-slate-600">Caricamento…</p></AdminShell>;

  return (
    <AdminShell actions={saveBtn}>
      <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-ink" data-testid="admin-back-dashboard"><ArrowLeft className="h-4 w-4" /> Tutte le auto</Link>
      <h1 className="mt-4 font-display text-3xl font-extrabold uppercase tracking-tight text-ink">{isNew ? "Nuova auto" : "Modifica auto"}</h1>
      <p className="mt-1 text-sm text-slate-600">Tutti i campi sono facoltativi: compila solo quelli che vuoi mostrare.</p>
      <form id="car-form" onSubmit={save} className="mt-8 space-y-6" data-testid="car-form">
        <Block title="Foto">
          <PhotoManager photos={form.photos} onChange={(p) => set("photos", p)} />
        </Block>
        <Block title="Dati principali">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Marca" name="make" placeholder="es. Porsche" {...f} />
            <Field label="Modello" name="model" placeholder="es. 911 Carrera" {...f} />
            <Field label="Versione / allestimento" name="version" {...f} />
            <Field label="Prezzo (€)" name="price" type="number" placeholder="vuoto = su richiesta" {...f} />
            <Field label="Stato" name="status" options={{ available: "Disponibile", reserved: "Prenotata", sold: "Venduta" }} {...f} />
          </div>
        </Block>
        <Block title="Dettagli tecnici">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Chilometri" name="km" type="number" {...f} />
            <Field label="Anno" name="year" type="number" {...f} />
            <Field label="Immatricolazione" name="registration" placeholder="es. 03/2021" {...f} />
            <Field label="Potenza (CV)" name="cv" type="number" {...f} />
            <Field label="Potenza (kW)" name="kw" type="number" {...f} />
            <Field label="Cilindrata (cc)" name="engine_cc" type="number" {...f} />
            <Field label="Carburante" name="fuel" options={pick(FUELS, it.fuel)} {...f} />
            <Field label="Cambio" name="gearbox" options={pick(GEARBOXES, it.gearbox)} {...f} />
            <Field label="Carrozzeria" name="body" options={pick(BODIES, it.body)} {...f} />
            <Field label="Colore" name="color" {...f} />
            <Field label="Porte" name="doors" type="number" {...f} />
            <Field label="Posti" name="seats" type="number" {...f} />
            <Field label="Proprietari" name="owners" type="number" {...f} />
            <Field label="Targa" name="plate" {...f} />
            <Field label="Telaio (VIN)" name="vin" {...f} />
          </div>
        </Block>
        <Block title="Descrizione e traduzioni">
          <DescriptionTranslator form={form} set={set} />
        </Block>
        <Block title="Pubblicazione">
          <div className="flex flex-col gap-4 sm:flex-row sm:gap-10">
            <label className="flex items-center gap-3 text-sm text-ink">
              <Switch checked={!!form.visible} onCheckedChange={(v) => set("visible", v)} data-testid="car-field-visible" /> Visibile sul sito
            </label>
            <label className="flex items-center gap-3 text-sm text-ink">
              <Switch checked={!!form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="car-field-featured" /> In evidenza (mostrata per prima)
            </label>
          </div>
        </Block>
        <div className="flex justify-end">{saveBtn}</div>
      </form>
    </AdminShell>
  );
}
